// src/components/audio/RetranscribeButton.js
//
// Small action button rendered next to FlaggedRowAudioButton. Opens a
// language picker; on selection, fires the Tauri sidecar's transcribe-one
// command, then writes the result back to the project's transcripts_meta
// document.
//
// SHIP 2 BEHAVIORAL CONTRACT
// ==========================
// - Re-transcribing does NOT mutate the project's primary CSV. The original
//   transcript stays as ground truth.
// - The new transcript is stored as `text_override` in transcripts_meta.cells.
//   The dashboard reads `text_override` (if present) instead of the original
//   response_preview when displaying the row.
// - The original transcript is logged to audio_notes with tag='language'
//   so there's a permanent audit trail.
// - The flag itself stays "stale" until the user re-runs quality checks.
//   A small "re-transcribed" badge tells them to refresh.
//
// Why text_override instead of mutating primary data?
// - Avoids any chance of corrupting the project's source-of-truth CSV
// - Cheaply reversible (delete the override to restore the original)
// - Easy to surface in the UI ("re-transcribed" badge)
// - Future: a "Promote overrides to primary" bulk action could lift these
//   into the actual CSV when the user is happy with them

import React, { useState, useRef, useEffect } from 'react';
import { Languages, Loader2, Check, X } from 'lucide-react';
import { resolveAudioPath } from './AudioPlayer';
import { DataService } from '../../services/dataService';

// ── Module-level keyframes injection ──────────────────────────────────────
// Hoisted out of render to avoid one <style> tag per flagged row. See
// matching block in InlineAudioCell.js for why this matters in production.
if (typeof document !== 'undefined' && !document.getElementById('retranscribe-keyframes')) {
  const tag = document.createElement('style');
  tag.id = 'retranscribe-keyframes';
  tag.textContent = '@keyframes rt-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(tag);
}

// Language list — production-locked to English only.
//
// Earlier iterations exposed Nyanja, Swahili, and Shona as "experimental"
// options. Field testing showed two problems:
//   1. Whisper rejects ISO codes like 'ny' (Nyanja) outright — they aren't in
//      its training language set, so the request errored before any audio
//      was processed.
//   2. When forced through auto-detect, Whisper produced confident-looking
//      but wholly fabricated transcripts in unrelated languages (e.g. Tagalog
//      tokens for Bemba audio). This is worse than no transcript at all
//      because reviewers can't distinguish real vs. hallucinated output.
//
// Until Whisper (or a fine-tuned variant) can handle these reliably, we
// limit re-transcription to English. Auto-detect is kept as a fallback for
// the rare case where a recording is genuinely English but flagged as a
// non-English-language file by the project default.
const LANGUAGES = [
  { code: 'en',   label: 'English',     reliable: true },
  { code: 'auto', label: 'Auto-detect', reliable: true },
];

const RetranscribeButton = ({
  resolvedAudio,           // { audioColumn, filename } from FlaggedRowAudioButton's resolver
  rowIndex,
  audioSettings,           // { media_dir, audio_language, ... }
  tauriApi,                // { invoke }
  isDarkMode,
  projectId,
  primaryData,             // for hint lookup
  transcriptsMeta,         // current meta doc, used to update
  onMetaUpdated,           // callback (newMeta) => void
  userInfo,                // for audit log author
  audioNotesDoc,           // current audio_notes doc, for audit log
  onAudioNotesUpdated,     // callback (newDoc) => void
}) => {
  const colors = isDarkMode
    ? { fg: '#9CA3AF', bg: 'rgba(255,255,255,0.04)', bd: 'rgba(255,255,255,0.10)',
        fgHover: '#FBBF24', bgHover: 'rgba(251,191,36,0.10)' }
    : { fg: '#6B7280', bg: 'rgba(0,0,0,0.04)',       bd: 'rgba(0,0,0,0.10)',
        fgHover: '#D97706', bgHover: 'rgba(217,119,6,0.08)' };

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const menuRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!resolvedAudio || !audioSettings?.media_dir || !tauriApi?.invoke || !projectId) return null;

  const handleSelect = async (code) => {
    setBusy(true);
    setError(null);
    try {
      const audioPath = resolveAudioPath(audioSettings.media_dir, resolvedAudio.filename);

      // Find the question label from primaryData if we can — used as Whisper
      // initial_prompt for better accuracy
      const prompt = lookupPromptForColumn(primaryData, resolvedAudio.audioColumn);

      const result = await tauriApi.invoke('run_transcribe_one', {
        args: {
          audio_path: audioPath,
          column:     resolvedAudio.audioColumn,
          row_index:  rowIndex,
          prompt:     prompt || null,
          language:   code === 'auto' ? null : code,
          model:      null,  // use sidecar default
        },
      });

      // result is the parsed `done_one` event
      const newText = result?.text || '';
      const newConf = result?.confidence ?? null;
      const newSegs = result?.segments ?? null;
      const langDetected = result?.language_detected ?? null;

      // ── Update transcripts_meta with text_override for this cell ──────────
      const key = `${rowIndex}|${resolvedAudio.audioColumn}`;
      const existingCell = transcriptsMeta?.cells?.[key] || {};
      const originalText = existingCell.text_override
        ? existingCell.text_original_at_first_override
        : (existingCell.text || null);

      const updatedMeta = {
        ...(transcriptsMeta || {}),
        cells: {
          ...(transcriptsMeta?.cells || {}),
          [key]: {
            ...existingCell,
            row_index:         rowIndex,
            column:            resolvedAudio.audioColumn,
            transcript_column: `${resolvedAudio.audioColumn}_transcript`,
            filename:          resolvedAudio.filename,
            // override fields — frontend uses these for display
            text_override:     newText,
            confidence:        newConf,           // overwrites with new confidence
            segments:          newSegs,
            language_detected: langDetected,
            language_hint:     code === 'auto' ? null : code,
            // preserve the very first original transcript for audit
            text_original_at_first_override: originalText,
            override_at:       new Date().toISOString(),
          },
        },
      };
      await DataService.saveTranscriptsMeta(projectId, updatedMeta);
      if (onMetaUpdated) onMetaUpdated(updatedMeta);

      // ── Log to audio_notes (audit trail) ──────────────────────────────────
      // Non-blocking — if this fails, the re-transcribe still succeeded
      try {
        const author = userInfo?.name || userInfo?.email || 'system';
        const lang = LANGUAGES.find(l => l.code === code);
        const langLabel = lang?.label || code;
        const noteText =
          `Re-transcribed in ${langLabel}. Original: ${originalText || '(empty)'}`;

        const newNote = {
          id: 'n_' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36),
          audio_path:        audioPath,
          audio_column:      resolvedAudio.audioColumn,
          submission_id:     null,  // not known here; fine
          timestamp_seconds: 0,
          text:              noteText,
          tag:               'language',
          created_at:        new Date().toISOString(),
          updated_at:        new Date().toISOString(),
          author:            author,
        };
        const updatedNotesDoc = {
          ...(audioNotesDoc || { notes: [], dispositions: {} }),
          notes: [...((audioNotesDoc && audioNotesDoc.notes) || []), newNote],
        };
        await DataService.saveAudioNotes(projectId, updatedNotesDoc);
        if (onAudioNotesUpdated) onAudioNotesUpdated(updatedNotesDoc);
      } catch (e) {
        console.warn('[RetranscribeButton] Audit log failed (non-fatal):', e);
      }

      setOpen(false);
    } catch (e) {
      console.error('[RetranscribeButton] Failed:', e);
      setError(typeof e === 'string' ? e : (e.message || 'Re-transcribe failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span ref={menuRef} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        disabled={busy}
        title="Re-transcribe in another language"
        style={{
          width: '22px', height: '22px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: open ? colors.bgHover : colors.bg,
          color: open ? colors.fgHover : colors.fg,
          border: `1px solid ${colors.bd}`,
          borderRadius: '5px',
          cursor: busy ? 'wait' : 'pointer',
          padding: 0,
          transition: 'all 0.12s ease',
        }}
        onMouseEnter={(e) => {
          if (busy || open) return;
          e.currentTarget.style.background = colors.bgHover;
          e.currentTarget.style.color = colors.fgHover;
        }}
        onMouseLeave={(e) => {
          if (busy || open) return;
          e.currentTarget.style.background = colors.bg;
          e.currentTarget.style.color = colors.fg;
        }}
      >
        {busy ? <Loader2 size={11} style={{ animation: 'rt-spin 0.9s linear infinite' }} />
              : <Languages size={11} />}
      </button>

      {open && !busy && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)', right: 0,
          minWidth: '160px',
          background: isDarkMode ? '#141516' : '#FFFFFF',
          border: `1px solid ${colors.bd}`,
          borderRadius: '8px',
          boxShadow: isDarkMode ? '0 6px 24px rgba(0,0,0,0.5)' : '0 6px 24px rgba(0,0,0,0.12)',
          zIndex: 100,
          padding: '6px',
          fontFamily: "'Outfit', system-ui, sans-serif",
        }}>
          <div style={{
            fontSize: '10px',
            color: colors.fg,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '4px 8px 6px',
          }}>
            Re-transcribe in
          </div>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={(e) => { e.stopPropagation(); handleSelect(l.code); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%',
                padding: '6px 8px',
                background: 'transparent',
                border: 'none',
                borderRadius: '5px',
                color: isDarkMode ? '#E7EAF0' : '#15181D',
                fontSize: '12.5px',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span>{l.label}</span>
              {!l.reliable && (
                <span title="Whisper has limited training data for this language; output quality may vary"
                      style={{ fontSize: '9.5px', color: colors.fg }}>
                  experimental
                </span>
              )}
            </button>
          ))}
          {error && (
            <div style={{
              marginTop: '6px',
              padding: '6px 8px',
              fontSize: '11px',
              color: '#DC2626',
              background: 'rgba(220,38,38,0.08)',
              borderRadius: '5px',
            }}>
              {error}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

// Heuristic: try to find the XLSForm question label for an audio column.
// We don't have direct access to the XLSForm here — but if primaryData has
// a `column_metadata` map keyed by column name, we can use that. Otherwise
// pass null and Whisper will run without an initial_prompt.
const lookupPromptForColumn = (primaryData, columnName) => {
  if (!primaryData) return null;
  const meta = primaryData.column_metadata || primaryData.columnMetadata;
  if (!meta || typeof meta !== 'object') return null;
  const entry = meta[columnName];
  if (!entry) return null;
  return entry.label || entry.question || null;
};

export default RetranscribeButton;